import { useTranslation } from 'react-i18next';
import { ColumnDef } from '@tanstack/react-table';
import { TableData } from '@anytag/shared-types';
import { formatIntNumber } from '@anytag/shared-utils';
import { InfluencerAvatar } from '@anytag/shared-molecules';
import { useGenerateInfluencerProfileLink } from '@anytag/shared-hooks';
import { Anchor, TableCell } from '@anytag/shared-atoms';
import { SocialAccountType, TiktokUserTagListPostsItemPayload, TiktokUserTagPostsSortOrder } from '@anytag/graphql';
import { PostCell } from '../../../shared';

type Props = Pick<TableData<TiktokUserTagListPostsItemPayload>, 'data'>;

export const useTiktokTagsAnalyticsPostsListTable = ({ data = [] }: Props) => {
  const { t } = useTranslation();
  const { generateProfileLink } = useGenerateInfluencerProfileLink();

  const columns: ColumnDef<TiktokUserTagListPostsItemPayload>[] = [
    {
      accessorKey: 'rank',
      header: t<string>('HeaderColumn.Rank'),
      meta: { align: 'center', sortField: TiktokUserTagPostsSortOrder.RANK },
      cell: ({ row }) => <div className="pr-2">{formatIntNumber(row.original.rank + 1)}</div>
    },
    {
      accessorKey: 'influencer',
      header: t<string>('HeaderColumn.Influencer'),
      cell: ({ row }) => {
        const { influencerId, accountId, avatar, name, followersCount } = row.original;
        const profileLink = generateProfileLink(influencerId, {
          filter: { id: String(accountId), sm: SocialAccountType.TIKTOK }
        });

        return (
          <div className="flex w-50 items-center gap-2">
            <InfluencerAvatar {...profileLink} size="40px" src={avatar} className="rounded-none" />
            <div>
              <Anchor {...profileLink} label={name || '-'} className="text-base" />
              <div className="mt-1 text-xxs">
                {t('Follower')}:&nbsp;{formatIntNumber(followersCount)}
              </div>
            </div>
          </div>
        );
      }
    },
    {
      accessorKey: 'post',
      header: t<string>('HeaderColumn.Post'),
      cell: ({ row }) => {
        const { postId, content, thumbnail } = row.original;

        return <PostCell postId={postId} content={content} thumbnail={thumbnail} />;
      }
    },
    {
      accessorKey: 'engagement',
      header: t<string>('HeaderColumn.Engagement'),
      meta: {
        align: 'right',
        sortField: TiktokUserTagPostsSortOrder.ENGAGEMENT
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.engagement)} />
    },
    {
      accessorKey: 'views',
      header: t<string>('HeaderColumn.View'),
      meta: { align: 'right', sortField: TiktokUserTagPostsSortOrder.VIEWS },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.views)} />
    },
    {
      accessorKey: 'likes',
      header: t<string>('HeaderColumn.Like'),
      meta: { align: 'right', sortField: TiktokUserTagPostsSortOrder.LIKES },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.likes)} />
    },
    {
      accessorKey: 'comments',
      header: t<string>('HeaderColumn.Comment'),
      meta: { align: 'right', sortField: TiktokUserTagPostsSortOrder.COMMENTS },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.comments)} />
    },
    {
      accessorKey: 'saves',
      header: t<string>('HeaderColumn.Save'),
      meta: { align: 'right', sortField: TiktokUserTagPostsSortOrder.SAVES },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.saves)} />
    },
    {
      accessorKey: 'shares',
      header: t<string>('HeaderColumn.Share'),
      meta: { align: 'right', sortField: TiktokUserTagPostsSortOrder.SHARES },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.shares)} />
    },
    {
      accessorKey: 'postDate',
      header: t<string>('HeaderColumn.Post date'),
      meta: { sortField: TiktokUserTagPostsSortOrder.POST_DATE },
      cell: ({ row }) => <TableCell.Date value={row.original.postDate} />
    }
  ];

  return { columns };
};
